import type { NavigationItem } from "@/app/navigation";
import { cx } from "@/shared/ui/primitives";
import { Link } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

const GAP = 8;

/**
 * "Impostazioni" in the collapsed sidebar: its icon opens a small menu with its
 * pages. The menu is fixed beside the sidebar, so the sidebar's scroll cannot clip it.
 */
export function SettingsFlyout({
  items,
  workspaceSlug,
  active,
}: {
  items: NavigationItem[];
  workspaceSlug: string;
  /** One of its pages is open. */
  active: boolean;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const open = position !== null;
  // Near the bottom of the screen the menu moves up to stay whole.
  useLayoutEffect(() => {
    const element = panel.current;
    if (!position || !element) return;
    const top = Math.max(
      GAP,
      Math.min(position.top, window.innerHeight - element.offsetHeight - GAP),
    );
    if (top !== position.top) setPosition({ ...position, top });
  }, [position]);
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>("a")?.focus();
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || button.current?.contains(target)) return;
      setPosition(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setPosition(null);
      button.current?.focus();
    };
    const closeOnResize = () => setPosition(null);
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnResize);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [open]);
  const toggle = () => {
    if (open) return setPosition(null);
    const rect = button.current?.getBoundingClientRect();
    // Beside the sidebar's edge, not over it.
    const edge = button.current?.closest(".workspace-nav")?.getBoundingClientRect().right;
    if (rect) setPosition({ top: rect.top, left: (edge ?? rect.right) + GAP });
  };
  return (
    <div className="nav-group">
      <button
        ref={button}
        type="button"
        className={cx("nav-link nav-group__toggle", active && "nav-link--active")}
        title="Impostazioni"
        aria-expanded={open}
        aria-controls={id}
        onClick={toggle}
      >
        <Settings aria-hidden="true" size={19} />
        <span className="nav-link__label">Impostazioni</span>
      </button>
      {position ? (
        <div
          ref={panel}
          id={id}
          className="nav-flyout"
          style={{ top: position.top, left: position.left }}
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (next && (panel.current?.contains(next) || button.current?.contains(next))) return;
            if (next) setPosition(null);
          }}
        >
          <p className="nav-flyout__title">Impostazioni</p>
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              params={{ workspaceSlug }}
              className="nav-link nav-link--child"
              activeProps={{ className: "nav-link nav-link--child nav-link--active" }}
              onClick={() => setPosition(null)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
